import { ApolloError, FetchResult } from '@apollo/client'
import { ErrorDetailsFragment, SimpleError, StructuredError } from 'generated/resources/graphql'
import camelCase from 'lodash/camelCase'

import app from 'modules/core/store/models/app'

import i18n from './i18n'

export type Result<TData> =
  | { success: true; data: TData }
  | {
      success: false
      errors: readonly ErrorDetails[]
    }

// NOTE: this symbol is meant to be hidden in this module so don't export
//       it to the outside please.
const resultKey: unique symbol = Symbol()

export type UnhandledResult<TResult> = { [resultKey]: TResult }

type ErrorDetails = StructuredError | SimpleError

const GENERIC_ERROR = 'generic_error'

export type MutationPayload = { errors: ErrorDetailsFragment[] }
export type HandleMutationResult<TData> = Promise<UnhandledResult<Result<Omit<TData, 'errors'>>>>

const buildUnhandledResult = <TData>(result: Result<TData>) =>
  ({ [resultKey]: result } as UnhandledResult<Result<TData>>)

export const handleMutation = async <TData, TOutput>(
  mutationPromise: Promise<FetchResult<TData>>,
  extractor: (data: TData) => TOutput & MutationPayload
): HandleMutationResult<TOutput> => {
  try {
    const result = await mutationPromise

    if (!result.data) {
      return Promise.resolve(
        buildUnhandledResult({
          success: false,
          errors: [{ type: GENERIC_ERROR, message: i18n.t('errors.generic_error') }],
        })
      )
    }

    const { errors: mutationErrors, ...data } = extractor(result.data)

    if (mutationErrors.length) {
      return Promise.resolve(
        buildUnhandledResult({
          success: false,
          errors: mutationErrors,
        })
      )
    }

    return buildUnhandledResult({
      success: true,
      data,
    })
  } catch (error) {
    if (error instanceof ApolloError) {
      // eslint-disable-next-line no-console
      console.error(error.message, error.stack)

      return Promise.resolve(
        buildUnhandledResult({
          success: false,
          errors: [{ type: GENERIC_ERROR, message: i18n.t('errors.generic_error') }],
        })
      )
    } else {
      throw error
    }
  }
}

const isErrorMessage = (error: SimpleError | StructuredError): error is SimpleError => {
  return !(error as StructuredError).field
}

const isFormError = (error: SimpleError | StructuredError): error is StructuredError => {
  return !!(error as StructuredError).field
}

export const handleResult = <TData>(
  result: UnhandledResult<Result<TData>>,
  fn: (errors: Record<string, string>) => void
): Result<TData> => {
  const handled = result[resultKey]

  if (!handled.success) {
    const errorMessages = handled.errors.filter(isErrorMessage)
    const formErrors = handled.errors.filter(isFormError)

    if (errorMessages.length) {
      app.setFlash({
        type: 'wrong',
        message: errorMessages.map(({ message }) => message).join(', '),
        time: 3000,
      })
    }

    if (formErrors.length) {
      fn(
        formErrors.reduce<Record<string, string>>((acc, { field, messages }) => {
          acc[camelCase(field)] = messages.join(', ')
          return acc
        }, {})
      )
    }
  }

  return handled
}
